import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import {
  SlideUpModalModel,
  SlideUpModalCtaBlock,
} from '~/pages/model-landing/store';
import {
  GetSessionSlideUpModalHide,
  SetSessionSlideUpModalHide,
} from '~/common/utils/session';
import { Button, SvgIcon } from '~/common/components/ui-elements';
import { useIsMobile } from '~/common/hooks/use-is-mobile';
import styles from './index.module.scss';
import digitalData from '~/common/services/data-layer-service';
import { Link } from '~/common/models';
import ImgLazyLoad from '../image-lazy-loading';
import brochureIcon from '~/assets/images/brochure.png';
import buildIcon from '~/assets/images/build.png';
import testDriveIcon from '~/assets/images/test-drive.png';
import locationIcon from '~/assets/images/location.png';
import valueMyCarIcon from '~/assets/images/value-my-car.png';
import bookServiceIcon from '~/assets/images/bookservice.png';
import browseVehiclesIcon from '~/assets/images/browse-vechicles.png';
import seeOffersIcon from '~/assets/images/see-offers.png';

const STRIP_ICONS: Record<string, string> = {
  'Build Your Own': buildIcon,
  'Book a Test Drive': testDriveIcon,
  'Download a Brochure': brochureIcon,
  'Find a Dealer': locationIcon,
  'Value My Car': valueMyCarIcon,
  'Book a Service': bookServiceIcon,
  Showroom: browseVehiclesIcon,
  'See Offers': seeOffersIcon,
};

interface SlideUpModalProps {
  slideUpModalContent?: SlideUpModalModel;
  isActive: boolean;
  modelName?: string;
}

export const SlideUpModal = React.memo<SlideUpModalProps>(
  ({ slideUpModalContent, isActive, modelName }) => {
    const [isSlideUpModalVisible, setSlideUpModalVisible] = useState<boolean>(
      isActive || false
    );
    const isSlideUpModalHidden =
      GetSessionSlideUpModalHide() === 1 ? true : false;
    const [isSlideUpModalClosed, setSlideUpModalClosed] =
      useState<boolean>(isSlideUpModalHidden);
    const { isMobileOrSmallTablet } = useIsMobile();
    const overlayRef = useRef<HTMLDivElement | null>(null);

    const hideSlideUpModal = () => {
      SetSessionSlideUpModalHide();
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');

      if (isSlideUpModalVisible) {
        setSlideUpModalVisible(false);
        setSlideUpModalClosed(
          GetSessionSlideUpModalHide() === 1 ? true : false
        );
      }
    };

    useEffect(() => {
      if (isSlideUpModalClosed) {
        document.body.style.removeProperty('overflow');
        document.documentElement.style.removeProperty('overflow');
        setSlideUpModalVisible(false);
      } else {
        if (isActive) {
          document.body.style.overflow = 'hidden';
          document.documentElement.style.overflow = 'hidden';
        }
        setSlideUpModalVisible(isActive);
      }
    }, [isActive]);

    // `overflow: hidden` alone doesn't block wheel/touch scroll momentum on
    // every browser (notably iOS Safari). Block scroll input on the overlay
    // itself (which covers the full viewport wherever the sheet doesn't) —
    // scoping the listener to that element, rather than the whole window,
    // means it can never interfere with the sheet's own scrolling.
    useEffect(() => {
      const overlayEl = overlayRef.current;
      if (!isSlideUpModalVisible || !overlayEl) return;

      const preventScroll = (e: Event) => e.preventDefault();

      overlayEl.addEventListener('wheel', preventScroll, { passive: false });
      overlayEl.addEventListener('touchmove', preventScroll, {
        passive: false,
      });

      return () => {
        overlayEl.removeEventListener('wheel', preventScroll);
        overlayEl.removeEventListener('touchmove', preventScroll);
      };
    }, [isSlideUpModalVisible]);

    const footerDescription = (
      slideUpModalContent?.slideUpModalFooterDescription || ''
    ).replace(/\{modelName\}/gi, modelName || '');

    const hasFooter =
      slideUpModalContent?.slideUpModalFooterCta ||
      slideUpModalContent?.slideUpModalFooterDescription;

    return (
      <>
        <div className={styles.slideUpModalSection}>
          <div
            ref={overlayRef}
            onClick={hideSlideUpModal}
            className={cn(styles.slideUpModalOverlay, {
              [styles.slideUpModalOverlayVisible]: isSlideUpModalVisible,
            })}
          />
          <div
            className={cn(styles.slideUpModalContent, {
              [styles.slideUpModalContentVisible]: isSlideUpModalVisible,
            })}
          >
            {(() => {
              const modelImageSrc = isMobileOrSmallTablet()
                ? slideUpModalContent?.slideUpModalImageMobile ||
                  slideUpModalContent?.slideUpModalImage
                : slideUpModalContent?.slideUpModalImage ||
                  slideUpModalContent?.slideUpModalImageMobile;

              return (
                modelImageSrc && (
                  <div className={styles.slideUpModalImage}>
                    <ImgLazyLoad loading="lazy" src={modelImageSrc} />
                  </div>
                )
              );
            })()}
            <div
              className={styles.slideUpModalBody}
              style={{
                backgroundColor:
                  slideUpModalContent?.slideUpModalBackgroundColor || undefined,
              }}
            >
              <div
                className={styles.slideUpModalCloseButton}
                onClick={hideSlideUpModal}
              >
                <SvgIcon
                  type="close"
                  color="#032660"
                  size={isMobileOrSmallTablet() ? '18px' : '30px'}
                />
              </div>
              <div
                className={styles.slideUpModalHeader}
                style={
                  {
                    '--slideUpModalHeaderColor':
                      slideUpModalContent?.slideUpModalHeaderFontColor ||
                      undefined,
                  } as React.CSSProperties
                }
                dangerouslySetInnerHTML={{
                  __html: slideUpModalContent?.slideUpModalHeader || '',
                }}
              />
              <div
                className={styles.slideUpModalDescription}
                style={
                  {
                    '--slideUpModalDescriptionColor':
                      slideUpModalContent?.slideUpModalDescriptionFontColor ||
                      undefined,
                  } as React.CSSProperties
                }
                dangerouslySetInnerHTML={{
                  __html: slideUpModalContent?.slideUpModalDescription || '',
                }}
              />
              {(() => {
                const ctaBlocks =
                  slideUpModalContent?.slideUpModalCtaBlocks?.slice(0, 3) || [];
                const emphasiseMiddle =
                  ctaBlocks.length > 2 && ctaBlocks.length % 2 !== 0;

                return (
                  ctaBlocks.length > 0 && (
                    <div className={styles.slideUpModalCtasSection}>
                      {ctaBlocks.map((currCta, index) => (
                        <SlideUpModalCta
                          key={index}
                          modelName={modelName}
                          isEmphasised={
                            emphasiseMiddle &&
                            index === Math.floor(ctaBlocks.length / 2)
                          }
                          {...currCta}
                        />
                      ))}
                    </div>
                  )
                );
              })()}
              {hasFooter && (
                <div
                  className={cn(styles.slideUpModalFooter, {
                    [styles.slideUpModalFooterHiddenOnMobile]:
                      slideUpModalContent?.slideUpModalHideFooterOnMobile,
                  })}
                  style={{
                    backgroundColor:
                      slideUpModalContent?.slideUpModalFooterBackgroundColor ||
                      undefined,
                  }}
                >
                  <div
                    className={styles.slideUpModalFooterDescription}
                    style={
                      {
                        '--slideUpModalFooterColor':
                          slideUpModalContent?.slideUpModalFooterFontColor ||
                          undefined,
                      } as React.CSSProperties
                    }
                    dangerouslySetInnerHTML={{ __html: footerDescription }}
                  />
                  {slideUpModalContent?.slideUpModalFooterCta && (
                    <Button
                      className={styles.slideUpModalFooterButton}
                      href={slideUpModalContent.slideUpModalFooterCta.url}
                      type={'primary'}
                      text={slideUpModalContent.slideUpModalFooterCta.name}
                      target={
                        slideUpModalContent.slideUpModalFooterCta.target ||
                        '_self'
                      }
                      buttonWidth="fit-text"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);

interface SlideUpModalCtaProps extends SlideUpModalCtaBlock {
  modelName?: string;
  isEmphasised?: boolean;
}

export const SlideUpModalCta = React.memo<SlideUpModalCtaProps>(
  ({ ...props }) => {
    const handleOnClick = (
      ctaDetails: Link | undefined,
      modelName?: string
    ) => {
      if (ctaDetails?.name.toLocaleLowerCase() === 'download a brochure') {
        digitalData.push({
          event: '_formNavigate',
          form: {
            name: 'download a brochure',
            stage: 'submitted',
            details: {
              vehicleSelected: [
                {
                  make: 'Subaru',
                  model: modelName,
                },
              ],
            },
          },
        });
      }

      window.open(ctaDetails?.url, ctaDetails?.target || '_self');
    };

    const stripIcon = props?.stripIcon ? STRIP_ICONS[props.stripIcon] : '';
    const { isMobileOrSmallTablet } = useIsMobile();
    const ctaImageSrc = isMobileOrSmallTablet()
      ? props?.imageMobile || props?.image
      : props?.image || props?.imageMobile;

    return (
      <div
        className={cn(styles.SlideUpModalCtaContainer, {
          [styles.SlideUpModalCtaContainerBordered]: props?.showBorder,
          [styles.SlideUpModalCtaContainerEmphasised]: props?.isEmphasised,
        })}
        onClick={() => handleOnClick(props?.ctaLink, props?.modelName)}
      >
        <div className={styles.SlideUpModalCtaImageContainer}>
          {ctaImageSrc && (
            <ImgLazyLoad
              loading="lazy"
              className={styles.SlideUpModalCtaImage}
              src={ctaImageSrc}
            />
          )}
        </div>
        <div
          className={styles.SlideUpModalCtaStrip}
          style={{ backgroundColor: props?.stripBackgroundColor || undefined }}
        >
          {stripIcon && (
            <div
              className={styles.SlideUpModalCtaStripIcon}
              style={{
                backgroundColor: props?.stripTextColor || undefined,
                WebkitMaskImage: `url(${stripIcon})`,
                maskImage: `url(${stripIcon})`,
              }}
            />
          )}
          <span
            className={styles.SlideUpModalCtaStripText}
            style={{ color: props?.stripTextColor || undefined }}
          >
            {props?.stripText}
          </span>
        </div>
      </div>
    );
  }
);
